import React from "react";
import { Box } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  selectUserData,
  selectUserToken,
} from "../../../../../../../../../features/userSlice";
import { setLoadingOverviewConversionsGeoTrafficData } from "../../../../../../../../../features/bizDevelopmentDashboardMobileSlice";
import { BizDevelopmentDashboardMobileEndPointLogic } from "../../../../../../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardMobileEndPointLogic";
import GraphSkeletonForTrendsMob from "../../../../../../../../AssetManagement/Overview/Trends/TrendsMobile/helpers/components/GraphSkeletonForTrendsMob";
import ConversionsDetails from "./ConversionsDetails";
import ConversionsSortWrap from "./ConversionsSortWrap";
import CVRChartWrap from "./ConversionsContent/CVRChartWrap";
import { useOverviewConversionsMobile } from "./helpers/hooks/useOverviewConversionsMobile";
import { OverviewConversionsMobileDataSetQueryIds } from "./helpers/funcs/overviewConversionsMobileQueryIds";

const Conversions = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const response = useSelector(selectUserData);
  const [searchParams] = useSearchParams();
  const startDate = React.useMemo(
    () => dayjs().subtract(30, "day").format("YYYY-MM-DD"),
    [],
  );
  const endDate = React.useMemo(() => dayjs().format("YYYY-MM-DD"), []);
  const websiteSharedUids = searchParams.get("workspace");
  const [dateType, setDateType] = React.useState("action_date");
  const [timeUnit, setTimeUnit] = React.useState("day");
  const isClickQuery = dateType === "click_date";
  const dispatch = useDispatch();
  const loadingOverviewConversionsMobileData = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboardMobile?.loadingOverviewConversionsMobileData,
  );

  const selectedDimensions = React.useMemo(() => {
    const uidList = websiteSharedUids?.split(",") ?? [];
    return (
      response?.data?.website_permissions?.flatMap((siteObj: any) => {
        const shared_uid = Object.keys(siteObj)[0];
        if (!uidList.includes(shared_uid)) return [];
        return (siteObj[shared_uid].dimensions ?? []).map(
          (dim: any) => dim.dimension_name,
        );
      }) ?? []
    );
  }, [response, websiteSharedUids]);

  const params = React.useMemo(() => {
    const baseParams = {
      oauthTokenHeader,
      queryId: isClickQuery
        ? OverviewConversionsMobileDataSetQueryIds.CLICK
        : OverviewConversionsMobileDataSetQueryIds.ACTION,
      startDate,
      endDate,
      timeUnit: "day",
      type: "site",
      websiteSharedUids: websiteSharedUids ? websiteSharedUids.split(",") : [],
      dimensions: selectedDimensions?.[0] ? [selectedDimensions[0]] : [],
    };
    if (isClickQuery) {
      return {
        ...baseParams,
        campaignId: "1",
        adgroupId: "1",
        matchtype: "all",
        kwId: "1",
        country: "all",
        trafficSource: "all",
      };
    }
    return baseParams;
  }, [
    oauthTokenHeader,
    isClickQuery,
    startDate,
    endDate,
    websiteSharedUids,
    selectedDimensions,
  ]);

  const isReady =
    startDate && endDate && websiteSharedUids && selectedDimensions.length > 0;

  useOverviewConversionsMobile({
    params: isReady ? params : null,
    autoFetch: !!isReady,
  });

  React.useEffect(() => {
    if (dateType !== "click_date") return;
    const fetchData = async () => {
      try {
        dispatch(setLoadingOverviewConversionsGeoTrafficData(true));
        await Promise.all([
          BizDevelopmentDashboardMobileEndPointLogic.getOverviewConversionsGeosMobile(
            oauthTokenHeader,
          ),
          BizDevelopmentDashboardMobileEndPointLogic.getOverviewConversionsTrafficSourcesMobile(
            oauthTokenHeader,
          ),
        ]);
      } finally {
        dispatch(setLoadingOverviewConversionsGeoTrafficData(false));
      }
    };
    fetchData();
  }, [dateType, oauthTokenHeader, dispatch]);

  React.useEffect(() => {
    if (!isReady) return;
    BizDevelopmentDashboardMobileEndPointLogic.getOverviewConversionsActivityNotesMobile(
      {
        oauthTokenHeader,
        websiteSharedUid: websiteSharedUids?.split(",") || [],
        fromDate: startDate,
        toDate: endDate,
      },
    );
  }, [oauthTokenHeader, websiteSharedUids, startDate, endDate, isReady]);

  React.useEffect(() => {
    if (!oauthTokenHeader) return;
    BizDevelopmentDashboardMobileEndPointLogic.getOverviewConversionsAllUserList(
      oauthTokenHeader,
    );
  }, [oauthTokenHeader]);

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      <ConversionsDetails
        dateType={dateType}
        setDateType={setDateType}
        setTimeUnit={setTimeUnit}
      />
      <ConversionsSortWrap
        dateType={dateType}
        endDate={endDate}
        startDate={startDate}
      />
      {loadingOverviewConversionsMobileData ? (
        Array.from({ length: 2 }).map((_, index) => (
          <GraphSkeletonForTrendsMob key={index} />
        ))
      ) : (
        <CVRChartWrap
          dateType={dateType}
          endDate={endDate}
          setTimeUnit={setTimeUnit}
          startDate={startDate}
          timeUnit={timeUnit}
        />
      )}
    </Box>
  );
};

export default Conversions;
